import { useState, useEffect } from 'react';
import type { Confession } from '../types/confession';
import { getRandomConfession } from '../lib/api';
import { hapticFeedback } from '../lib/telegram';
import { ConfessionCard } from './ConfessionCard';

interface RandomButtonProps {
  onClose: () => void;
}

export function RandomConfessionModal({ onClose }: RandomButtonProps) {
  const [confession, setConfession] = useState<Confession | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadRandom = async () => {
    setIsLoading(true);
    setError(null);
    hapticFeedback('medium');

    try {
      const data = await getRandomConfession();
      setConfession(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load random confession');
    } finally {
      setIsLoading(false);
    }
  };

  // Load on mount
  useEffect(() => {
    loadRandom();
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/80 animate-fade-in">
      <div className="w-full max-w-md bg-[#1a1a2e] rounded-t-2xl sm:rounded-2xl p-4 pb-8 animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold flex items-center gap-2">
            🎲 Random Confession
          </h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        {isLoading && (
          <div className="py-12 text-center">
            <div className="text-4xl mb-4 animate-bounce">🎰</div>
            <p className="text-gray-400">Finding a random confession...</p>
          </div>
        )}

        {error && !isLoading && (
          <div className="py-8 text-center">
            <p className="text-4xl mb-4">😵</p>
            <p className="text-gray-400 mb-4">{error}</p>
            <button
              onClick={loadRandom}
              className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-full text-sm transition-colors"
            >
              Try Again
            </button>
          </div>
        )}

        {confession && !isLoading && (
          <>
            <ConfessionCard confession={confession} />
            <button
              onClick={loadRandom}
              className="w-full mt-4 px-4 py-3 bg-purple-600 hover:bg-purple-500 rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
            >
              <span>🎲</span>
              <span>Another One!</span>
            </button>
          </>
        )}
      </div>
    </div>
  );
}

interface RandomButtonFloatingProps {
  onClick: () => void;
}

export function RandomButtonFloating({ onClick }: RandomButtonFloatingProps) {
  return (
    <button
      onClick={onClick}
      className="fixed bottom-20 right-4 w-14 h-14 bg-purple-600 hover:bg-purple-500 rounded-full shadow-lg shadow-purple-600/30 flex items-center justify-center text-2xl transition-all hover:scale-110 active:scale-95 z-40"
    >
      🎲
    </button>
  );
}
